import type {
  ThreadPullRequestKey,
  ThreadPullRequestLink,
  ThreadPullRequestLinkSource,
  ThreadPullRequestObservation,
  ThreadPullRequestStack,
} from "@afar-research/contracts";

import {
  normalizeThreadPullRequestKey,
  threadPullRequestKeyOf,
  threadPullRequestKeysEqual,
} from "./threadPullRequests.ts";

/**
 * Every transition of a thread's pull request collection. The Environment Service persists
 * these decisions as events; it does not re-derive them, and no client writes membership.
 *
 * Concurrency has two levels. The collection `revision` guards a user's link and unlink
 * commands against a competing client. A membership's `generation` is the revision at which
 * it became visible, which makes it unique for the life of the collection: a read that
 * started before an unlink carries the old generation and can never be applied to the
 * membership a later relink created. Observations are guarded by generation alone, so a
 * routine refresh never conflicts with an unrelated user command.
 */

export interface ThreadPullRequestCollection {
  readonly links: ReadonlyArray<ThreadPullRequestLink>;
  readonly revision: number;
}

export const emptyThreadPullRequestCollection: ThreadPullRequestCollection = {
  links: [],
  revision: 0,
};

/**
 * The collection a thread carries. A thread that predates the collection, or a payload from a
 * client that does not know about it, is empty at revision zero: this is the only place that
 * default is applied.
 */
export function threadPullRequestCollectionOf(thread: {
  readonly pullRequests?: ReadonlyArray<ThreadPullRequestLink> | undefined;
  readonly pullRequestsRevision?: number | undefined;
}): ThreadPullRequestCollection {
  return {
    links: thread.pullRequests ?? [],
    revision: thread.pullRequestsRevision ?? 0,
  };
}

/** A read that succeeded and found nothing is `null`; one that failed is `"unavailable"`. */
export type ThreadPullRequestReadResult<A> = A | null | "unavailable";

export interface ThreadPullRequestLinkCommand {
  readonly key: ThreadPullRequestKey;
  readonly url: string;
  readonly source: ThreadPullRequestLinkSource;
  readonly at: string;
}

export type ThreadPullRequestLinkDecision =
  | {
      readonly kind: "linked";
      readonly link: ThreadPullRequestLink;
      readonly collection: ThreadPullRequestCollection;
    }
  | {
      readonly kind: "restored";
      readonly link: ThreadPullRequestLink;
      readonly collection: ThreadPullRequestCollection;
    }
  | {
      readonly kind: "already-linked";
      readonly link: ThreadPullRequestLink;
      readonly collection: ThreadPullRequestCollection;
    }
  /** Stack discovery reached a tombstone. The collection is unchanged. */
  | { readonly kind: "dismissed-target"; readonly collection: ThreadPullRequestCollection };

export type ThreadPullRequestUnlinkDecision =
  | {
      readonly kind: "unlinked";
      readonly link: ThreadPullRequestLink;
      readonly collection: ThreadPullRequestCollection;
    }
  | {
      readonly kind: "dismissed";
      readonly link: ThreadPullRequestLink;
      readonly collection: ThreadPullRequestCollection;
    }
  | { readonly kind: "not-found"; readonly collection: ThreadPullRequestCollection };

export type ThreadPullRequestObservationDecision =
  | {
      readonly kind: "updated";
      readonly link: ThreadPullRequestLink;
      readonly collection: ThreadPullRequestCollection;
    }
  /** The read is current but says nothing new, so there is nothing to persist. */
  | { readonly kind: "unchanged"; readonly collection: ThreadPullRequestCollection }
  /** The membership this read addressed is gone, tombstoned, or was replaced. */
  | { readonly kind: "stale"; readonly collection: ThreadPullRequestCollection };

export type ThreadPullRequestDiscoveryDecision =
  | {
      readonly kind: "discovered";
      readonly added: ReadonlyArray<ThreadPullRequestLink>;
      readonly collection: ThreadPullRequestCollection;
    }
  | { readonly kind: "unchanged"; readonly collection: ThreadPullRequestCollection }
  | { readonly kind: "stale"; readonly collection: ThreadPullRequestCollection };

const isVisible = (link: ThreadPullRequestLink) => link.source !== "stack-dismissed";

function findLink(
  collection: ThreadPullRequestCollection,
  key: ThreadPullRequestKey,
): ThreadPullRequestLink | undefined {
  return collection.links.find((link) => threadPullRequestKeysEqual(link, key));
}

function withLinks(
  collection: ThreadPullRequestCollection,
  links: ReadonlyArray<ThreadPullRequestLink>,
): ThreadPullRequestCollection {
  return { links, revision: collection.revision + 1 };
}

function replace(
  collection: ThreadPullRequestCollection,
  key: ThreadPullRequestKey,
  next: ThreadPullRequestLink,
): ReadonlyArray<ThreadPullRequestLink> {
  return collection.links.map((link) => (threadPullRequestKeysEqual(link, key) ? next : link));
}

/**
 * Links a pull request, or restores one an earlier dismissal tombstoned. A restoration keeps
 * the original link time and observations, because the user is recovering the membership they
 * had rather than creating a new one; it takes a fresh generation all the same, so reads
 * issued before the dismissal cannot land on it. Stack discovery may not restore a tombstone.
 */
export function decideThreadPullRequestLink(
  collection: ThreadPullRequestCollection,
  command: ThreadPullRequestLinkCommand,
): ThreadPullRequestLinkDecision {
  const key = normalizeThreadPullRequestKey(command.key);
  const existing = findLink(collection, key);
  if (existing !== undefined && isVisible(existing)) {
    return { kind: "already-linked", link: existing, collection };
  }
  if (existing !== undefined && command.source === "stack") {
    return { kind: "dismissed-target", collection };
  }
  const generation = collection.revision + 1;
  if (existing !== undefined) {
    const restored: ThreadPullRequestLink = {
      ...existing,
      url: command.url,
      source: command.source,
      generation,
    };
    return {
      kind: "restored",
      link: restored,
      collection: withLinks(collection, replace(collection, key, restored)),
    };
  }
  const link: ThreadPullRequestLink = {
    ...key,
    url: command.url,
    source: command.source,
    linkedAt: command.at,
    generation,
    observation: null,
    stack: null,
  };
  return { kind: "linked", link, collection: withLinks(collection, [...collection.links, link]) };
}

/**
 * A pull request AFAR knows to belong to a native stack: it was discovered through one, it
 * carries its own stack metadata, or another member of the same repository lists it. Removing
 * one of those is a dismissal, so a later discovery does not put it back. How the user came to
 * link it does not matter — a manually linked stack member dismisses like any other.
 */
export function isKnownThreadPullRequestStackMember(
  collection: ThreadPullRequestCollection,
  link: ThreadPullRequestLink,
): boolean {
  if (link.source === "stack" || link.stack !== null) return true;
  const key = normalizeThreadPullRequestKey(link);
  return collection.links.some((other) => {
    if (other.stack === null || threadPullRequestKeysEqual(other, key)) return false;
    const otherKey = normalizeThreadPullRequestKey(other);
    if (otherKey.host !== key.host || otherKey.repository !== key.repository) return false;
    return other.stack.layers.some((layer) => layer.number === key.number);
  });
}

export function decideThreadPullRequestUnlink(
  collection: ThreadPullRequestCollection,
  command: { readonly key: ThreadPullRequestKey; readonly at: string },
): ThreadPullRequestUnlinkDecision {
  const key = normalizeThreadPullRequestKey(command.key);
  const existing = findLink(collection, key);
  if (existing === undefined || !isVisible(existing)) {
    return { kind: "not-found", collection };
  }
  if (isKnownThreadPullRequestStackMember(collection, existing)) {
    const dismissed: ThreadPullRequestLink = { ...existing, source: "stack-dismissed" };
    return {
      kind: "dismissed",
      link: dismissed,
      collection: withLinks(collection, replace(collection, key, dismissed)),
    };
  }
  return {
    kind: "unlinked",
    link: existing,
    collection: withLinks(
      collection,
      collection.links.filter((link) => !threadPullRequestKeysEqual(link, key)),
    ),
  };
}

export interface ThreadPullRequestObservationCommand {
  readonly key: ThreadPullRequestKey;
  /** The generation the read was issued against. */
  readonly generation: number;
  readonly observation: ThreadPullRequestReadResult<ThreadPullRequestObservation>;
  readonly stack: ThreadPullRequestReadResult<ThreadPullRequestStack>;
  readonly at: string;
}

function observationsEqual(
  left: ThreadPullRequestObservation | null,
  right: ThreadPullRequestObservation | null,
): boolean {
  if (left === null || right === null) return left === right;
  // `observedAt` is when the read happened, not what it found: a poll that sees the same pull
  // request must not count as a change, or every tick would persist an event.
  const { observedAt: _leftObservedAt, ...leftRest } = left;
  const { observedAt: _rightObservedAt, ...rightRest } = right;
  return JSON.stringify(leftRest) === JSON.stringify(rightRest);
}

/**
 * Applies what a read found. A failed read is not an answer: it keeps the previous observation
 * and stack, and only records that the pull request is currently unavailable. A successful read
 * that found no stack clears stack metadata, which a failed one must never do.
 */
export function decideThreadPullRequestObservation(
  collection: ThreadPullRequestCollection,
  command: ThreadPullRequestObservationCommand,
): ThreadPullRequestObservationDecision {
  const key = normalizeThreadPullRequestKey(command.key);
  const existing = findLink(collection, key);
  if (
    existing === undefined ||
    !isVisible(existing) ||
    existing.generation !== command.generation
  ) {
    return { kind: "stale", collection };
  }
  const failed = command.observation === "unavailable";
  const observation = failed
    ? existing.observation
    : (command.observation as ThreadPullRequestObservation | null);
  const stack =
    command.stack === "unavailable"
      ? existing.stack
      : (command.stack as ThreadPullRequestStack | null);
  const next: ThreadPullRequestLink = {
    ...existing,
    observation,
    stack,
    observationUnavailable: failed,
  };
  const unchanged =
    observationsEqual(existing.observation ?? null, observation ?? null) &&
    JSON.stringify(existing.stack) === JSON.stringify(stack) &&
    (existing.observationUnavailable ?? false) === failed;
  if (unchanged) return { kind: "unchanged", collection };
  return {
    kind: "updated",
    link: next,
    collection: withLinks(collection, replace(collection, key, next)),
  };
}

export interface ThreadPullRequestDiscoveryCommand {
  readonly anchorKey: ThreadPullRequestKey;
  readonly anchorGeneration: number;
  readonly siblings: ReadonlyArray<{
    readonly key: ThreadPullRequestKey;
    readonly url: string;
  }>;
  readonly at: string;
}

/**
 * Adds the stack siblings a read found. Discovery only ever adds: a member a later stack stops
 * listing stays linked, because the user's collection is not the host's stack. Tombstones are
 * re-checked here rather than when the read started, so a dismissal made while the read was in
 * flight still wins.
 */
export function decideThreadPullRequestStackDiscovery(
  collection: ThreadPullRequestCollection,
  command: ThreadPullRequestDiscoveryCommand,
): ThreadPullRequestDiscoveryDecision {
  const anchorKey = normalizeThreadPullRequestKey(command.anchorKey);
  const anchor = findLink(collection, anchorKey);
  if (
    anchor === undefined ||
    !isVisible(anchor) ||
    anchor.generation !== command.anchorGeneration
  ) {
    return { kind: "stale", collection };
  }
  const added: Array<ThreadPullRequestLink> = [];
  const seen = new Set(collection.links.map((link) => threadPullRequestKeyOf(link)));
  let revision = collection.revision;
  for (const sibling of command.siblings) {
    const key = normalizeThreadPullRequestKey(sibling.key);
    const identity = threadPullRequestKeyOf(key);
    if (seen.has(identity)) continue;
    seen.add(identity);
    revision += 1;
    added.push({
      ...key,
      url: sibling.url,
      source: "stack",
      linkedAt: command.at,
      generation: revision,
      observation: null,
      stack: null,
    });
  }
  if (added.length === 0) return { kind: "unchanged", collection };
  return {
    kind: "discovered",
    added,
    collection: { links: [...collection.links, ...added], revision },
  };
}
